import type {NotificationData} from '@mantine/notifications';
import { notifications} from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react';
import React from 'react';

export type AlertType = 'success' | 'error' | 'info' | 'warning';

interface AlertOptions {
  title?: string;
  message: string;
  type?: AlertType;
  timeout?: number | null;
}

const DEFAULT_TIMEOUT = 5000;
const ERROR_TIMEOUT = 10000;

const getNotificationColor = (type: AlertType): string => {
  const colorMap: Record<AlertType, string> = {
    success: 'green',
    error: 'red',
    warning: 'yellow',
    info: 'blue',
  };
  return colorMap[type];
};

const getNotificationIcon = (type: AlertType): React.ReactNode => {
  const iconMap: Record<AlertType, React.ReactNode> = {
    success: <IconCheck size="1.1rem" />,
    error: <IconX size="1.1rem" />,
    warning: <IconAlertCircle size="1.1rem" />,
    info: <IconInfoCircle size="1.1rem" />,
  };
  return iconMap[type];
};

const getNotificationTitle = (
  title: string | undefined,
  type: AlertType
): string => {
  return title || type.charAt(0).toUpperCase() + type.slice(1);
};

const getAutoCloseValue = (
  type: AlertType,
  timeout: number | null
): number | false => {
  if (type === 'error') return ERROR_TIMEOUT;
  return timeout === null ? false : timeout;
};

const createNotificationConfig = ({
  title,
  message,
  type = 'info',
  timeout = DEFAULT_TIMEOUT,
}: AlertOptions): NotificationData => ({
  title: getNotificationTitle(title, type),
  message,
  color: getNotificationColor(type),
  icon: getNotificationIcon(type),
  autoClose: getAutoCloseValue(type, timeout),
  position: 'top-right',
});

/** Fires a notification outside React — same colour/icon mapping as useAlert. */
export const showAlert = (
  message: string,
  type: AlertType = 'info',
  title?: string
): void => {
  notifications.show(createNotificationConfig({message, type, title}));
};

import {useCallback} from 'react';

const useAlert = () => {
  const showNotification = useCallback((options: AlertOptions) => {
    const config = createNotificationConfig(options);
    notifications.show(config);
  }, []);

  const setAlert = useCallback(
    (
      message: string,
      type?: AlertType,
      timeout?: number | null,
      title?: string
    ) => {
      showNotification({message, type, timeout, title});
    },
    [showNotification]
  );

  return {
    setAlert,
  };
};

export {useAlert};
